/**
 * Template Library – Types & Constants
 *
 * All templates are stored as FHIR Questionnaire resources in Medplum.
 * Categorization uses `Questionnaire.meta.tag` with our custom system.
 * Status lifecycle maps to `Questionnaire.status` (draft | active | retired).
 */
import type { Questionnaire } from '@medplum/fhirtypes';

// ---------------------------------------------------------------------------
// Template category (maps to tabs in Template Library UI)
// ---------------------------------------------------------------------------

/** The custom tag system used to categorize templates by tab. */
export const TEMPLATE_CATEGORY_SYSTEM = 'https://unify.health/fhir/CodeSystem/template-category';

/** Allowed category codes — each maps to a Template Library tab. */
export const TEMPLATE_CATEGORIES = [
  'appointments',
  'measures',
  'treatment-planning',
  'forms',
  'administrative',
] as const;

export type TemplateCategory = (typeof TEMPLATE_CATEGORIES)[number];

/** Human-readable labels for each category tab. */
export const TEMPLATE_CATEGORY_LABELS: Record<TemplateCategory, string> = {
  appointments: 'Appointments',
  measures: 'Measures',
  'treatment-planning': 'Treatment Planning',
  forms: 'Forms',
  administrative: 'Administrative',
};

/** Descriptions shown under each tab header. */
export const TEMPLATE_CATEGORY_DESCRIPTIONS: Record<TemplateCategory, string> = {
  appointments:
    'Documentation associated with client interactions and care delivery events.',
  measures: 'Data and tools used to evaluate, monitor, and inform client health and outcomes.',
  'treatment-planning':
    'Records that guide clinical decision-making and the overall direction of care.',
  forms: 'Standardized documents used for administrative, legal, and compliance purposes.',
  administrative: 'Templates for internal documentation like supervision notes.',
};

// ---------------------------------------------------------------------------
// Template status (lifecycle)
// ---------------------------------------------------------------------------

/**
 * Template lifecycle status.
 * Maps to FHIR Questionnaire.status:
 *   draft → "draft"
 *   active → "active"
 *   archived → "retired"
 */
export type TemplateStatus = 'draft' | 'active' | 'retired';

/** Human-readable labels for each status. */
export const TEMPLATE_STATUS_LABELS: Record<TemplateStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  retired: 'Archived',
};

/** Map our UI status names to FHIR Questionnaire.status values. */
export const STATUS_TO_FHIR: Record<'draft' | 'active' | 'archived', Questionnaire['status']> = {
  draft: 'draft',
  active: 'active',
  archived: 'retired',
};

export const FHIR_TO_STATUS: Record<string, 'draft' | 'active' | 'archived'> = {
  draft: 'draft',
  active: 'active',
  retired: 'archived',
};

// ---------------------------------------------------------------------------
// Scored measure extensions
// ---------------------------------------------------------------------------

/** Tag system for scored measure delivery mode (patient-facing vs clinician-facing). */
export const SCORED_MEASURE_DELIVERY_SYSTEM = 'https://unify.health/fhir/scored-measure-delivery';

/** Tag system for scored measure scoring method. */
export const SCORED_MEASURE_SCORING_SYSTEM = 'https://unify.health/fhir/scored-measure-scoring';

export type ScoredMeasureDelivery = 'patient-facing' | 'clinician-facing';
export type ScoredMeasureScoringMethod = 'client-side' | 'server-side-bot' | 'extract-mapping';

/**
 * Score range extensions for scored-measure templates.
 * Stored on Questionnaire.extension. Used in Measures tab to color score badges.
 * Bad (red) = top of range (high scores); Good (green) = bottom (low scores).
 * Interpretation: score <= goodMax → green, score <= okMax → yellow, else red.
 */
export const SCORE_RANGE_GOOD_MAX_EXTENSION = 'https://unify.health/fhir/StructureDefinition/score-range-good-max';
export const SCORE_RANGE_OK_MAX_EXTENSION = 'https://unify.health/fhir/StructureDefinition/score-range-ok-max';
export const SCORE_RANGE_TOTAL_EXTENSION = 'https://unify.health/fhir/StructureDefinition/score-range-total';

/** Extension URL for treatment-plan template default diagnosis (ICD-10 code). Description is derived from code. */
export const TREATMENT_PLAN_DEFAULT_DIAGNOSIS_EXTENSION =
  'https://unify.health/fhir/StructureDefinition/treatment-plan-default-diagnosis-code';

/** How total assessment score is derived from item scores (e.g. sum of item scores). */
export const SCORE_CALCULATION_METHODS = ['sum'] as const;
export type ScoreCalculationMethod = (typeof SCORE_CALCULATION_METHODS)[number];

export const SCORE_CALCULATION_LABELS: Record<ScoreCalculationMethod, string> = {
  sum: 'Sum',
};

export interface ScoreRanges {
  /** Total score for the assessment (e.g. 27 for PHQ-9). Used for display and for Bad row upper bound. */
  totalMax?: number;
  /** Inclusive max for "good" (green). Scores 0..goodMax are good. */
  goodMax?: number;
  /** Inclusive max for "ok" (yellow). Scores (goodMax+1)..okMax are ok. Scores above okMax are bad (red). */
  okMax?: number;
}

// ---------------------------------------------------------------------------
// Freeze-per-note reference model
// ---------------------------------------------------------------------------

/**
 * When a clinician creates a note/document, the note must store these references
 * to the exact template version used at creation time.
 */
export interface TemplateVersionReference {
  /** Canonical template reference: Questionnaire.url or Questionnaire/{id} */
  templateCanonical: string;
  /** The versionId at the time of note creation (immutable pointer) */
  templateVersionId: string;
  /** Convenience: Questionnaire.title at the time of creation */
  templateDisplayName?: string;
}

// ---------------------------------------------------------------------------
// Template settings (stored in Medplum as configuration)
// ---------------------------------------------------------------------------

/** Standard document format settings. */
export interface StandardDocumentFormat {
  /** Whether to include the practice logo in documents */
  includePracticeLogo: boolean;
  /** Footer information (multi-line) */
  footerText: string;
  /** Reference to the logo Binary resource in Medplum */
  logoBinaryId?: string;
}

/** Pre-licensed clinician document format settings. */
export interface PreLicensedClinicianFormat {
  /** Toggle: add supervisor info to notes and billing documents */
  addSupervisorInfo: boolean;
  /** Footer information for pre-licensed documents (supports {{variable}} tokens) */
  footerText: string;
}

// ---------------------------------------------------------------------------
// Template settings resource identifier
// ---------------------------------------------------------------------------

/**
 * Template settings are stored as a FHIR Basic resource with a specific code
 * so we can search for it deterministically.
 */
export const TEMPLATE_SETTINGS_SYSTEM = 'https://unify.health/fhir/template-settings-resource-type';
export const TEMPLATE_SETTINGS_CODE = 'template-settings';

// ---------------------------------------------------------------------------
// Documentation settings (full practice-level configuration)
// ---------------------------------------------------------------------------

/** Contexts where templates are used at runtime. */
export const TEMPLATE_USAGE_TARGETS = [
  'appointment-note',
  'medical-record-note',
  'assessment',
  'client-portal-form',
  'treatment-plan',
  'safety-plan',
  'risk-assessment',
  'admin-note',
  'discharge-summary',
  'supervision-review',
  'document-preview',
  'chart-document',
] as const;

export type TemplateUsageTarget = (typeof TEMPLATE_USAGE_TARGETS)[number];

export const TEMPLATE_USAGE_TARGET_LABELS: Record<TemplateUsageTarget, string> = {
  'appointment-note': 'Appointment note',
  'medical-record-note': 'Chart note',
  assessment: 'Assessment / scored measure',
  'client-portal-form': 'Client portal form',
  'treatment-plan': 'Treatment plan',
  'safety-plan': 'Safety plan',
  'risk-assessment': 'Risk assessment',
  'admin-note': 'Admin note',
  'discharge-summary': 'Discharge summary',
  'supervision-review': 'Supervision review',
  'document-preview': 'Document preview',
  'chart-document': 'Chart document (treatment plan, safety plan, etc.)',
};

export interface TemplateRules {
  includeDraftsInRuntime: boolean;
}

export interface DefaultTemplateEntry {
  target: TemplateUsageTarget;
  category?: TemplateCategory;
  templateId: string;
}

/**
 * Full documentation settings model persisted on the practice Organization
 * (extension) with legacy Basic fallback on read.
 */
export interface DocumentationSettings {
  enabledCategories: Record<TemplateCategory, boolean>;
  documentFormat: {
    standardFormat: StandardDocumentFormat;
    preLicensedFormat: PreLicensedClinicianFormat;
  };
  templateRules: TemplateRules;
  defaultTemplateByContext: DefaultTemplateEntry[];
}

export const DEFAULT_TEMPLATE_RULES: TemplateRules = {
  includeDraftsInRuntime: false,
};

export const DEFAULT_ENABLED_CATEGORIES: Record<TemplateCategory, boolean> = {
  appointments: true,
  measures: true,
  'treatment-planning': true,
  forms: true,
  administrative: true,
};

export const DEFAULT_DOCUMENTATION_SETTINGS: DocumentationSettings = {
  enabledCategories: { ...DEFAULT_ENABLED_CATEGORIES },
  documentFormat: {
    standardFormat: { includePracticeLogo: false, footerText: '' },
    preLicensedFormat: { addSupervisorInfo: false, footerText: '' },
  },
  templateRules: { ...DEFAULT_TEMPLATE_RULES },
  defaultTemplateByContext: [],
};

/** Extension URL for the JSON blob stored on the Basic settings resource. */
export const TEMPLATE_SETTINGS_DATA_EXT_URL = 'https://unify.health/fhir/StructureDefinition/template-settings-data';

/** Extension URLs for frozen template version on QuestionnaireResponse. */
export const FROZEN_TEMPLATE_CANONICAL_EXT = 'https://unify.health/fhir/template-canonical';
export const FROZEN_TEMPLATE_VERSION_EXT = 'https://unify.health/fhir/template-version-id';
export const FROZEN_TEMPLATE_DISPLAY_EXT = 'https://unify.health/fhir/template-display-name';

// ---------------------------------------------------------------------------
// Helper type for template list items
// ---------------------------------------------------------------------------

/** Denormalized template row for the table UI. */
export interface TemplateListItem {
  id: string;
  title: string;
  description: string;
  status: TemplateStatus;
  category: TemplateCategory;
  lastUpdated: string;
  versionId: string;
  /** Whether this template is the default for its category */
  isDefault: boolean;
  /** Whether this template is currently in use by existing notes */
  inUse: boolean;
  /** The underlying FHIR Questionnaire resource */
  resource: Questionnaire;
}
